import { useEffect, useRef, useState } from 'react';

import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Grid, Stack } from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useMutation } from '@redwoodjs/web';

import { TextAreaInput } from 'src/components';
import { InputBaseField } from 'src/components/common/form/FormInput';
import { SelectBaseField } from 'src/components/common/form/FormSelect';
import TextEditor from 'src/components/common/TextEditor';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { CREATE_MCC_MUTATION } from 'src/graphql/mutations/mccs.mutation';
import { GET_MCCS } from 'src/graphql/queries/mccs.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';

import MainCard from '../../components/MainCard/MainCard';

interface CreateMccInput {
    name: string;
    code: string;
    risk: RISK;
    formattedDescription?: {
        type: string;
        text: string;
    }[];
    formattedDocuments?: {
        type: string;
        text: string;
    }[];
    mccDescription: string;
}

export enum RISK {
    LOW,
    MEDIUM,
    HIGH,
    RESTRICTED,
    PROHIBITED,
}

const AddMccPage = () => {
    const timeout = useRef(null);
    const [, setDescriptionErrorMessage] = useState('');
    const [documentsTextValues, setDocumentsTextValues] = useState();
    const [documentsErrorMessage, setDocumentsErrorMessage] = useState('');

    const [createMcc, { loading }] = useMutation(CREATE_MCC_MUTATION, {
        onCompleted: (data) => {
            showSuccessToast(`${data.createMcc.code} category code created`);
        },
        refetchQueries: [
            {
                query: GET_MCCS,
                variables: { input: {}, page: { page: 0, pageSize: 5 } },
            },
        ],
        onError: (error) => {
            showErrorToast(error.message);
        },
    });

    const onSubmit = (input: CreateMccInput, { resetForm }) => {
        setDescriptionErrorMessage('');
        setDocumentsErrorMessage('');
        const { mccDescription, ...remainingInput } = input;
        const data = { ...remainingInput };
        const formattedDescriptionValue = [
            {
                type: 'string',
                text: mccDescription,
            },
        ];

        data.formattedDescription = formattedDescriptionValue;
        data.formattedDocuments = documentsTextValues;

        if (!data.formattedDescription) {
            setDescriptionErrorMessage('Description is ');
            return;
        }
        if (!data.formattedDocuments) {
            setDocumentsErrorMessage('Documents is required');
            return;
        }

        createMcc({ variables: { input: data } });
        resetForm(initialValues);
        timeout.current = setTimeout(() => {
            return navigate('/mccs');
        }, 1000);
    };

    useEffect(() => {
        return () => {
            return clearTimeout(timeout.current);
        };
    }, [timeout]);

    const initialValues = {
        name: '',
        code: null,
        risk: null,
        mccDescription: null,
        documents: 'N/A',
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().required('Name is required'),
        code: Yup.string()
            .matches(/^[0-9]{4}$/, 'Must be exactly 4 digits')
            .min(4)
            .required('Code is required')
            .nullable(),
        risk: Yup.string().required('Risk is required').nullable(),
        mccDescription: Yup.string().required('Description is required.').nullable(),
    });

    const riskMenuItems = [
        { key: 'LOW', value: 'Low' },
        { key: 'MEDIUM', value: 'Medium' },
        { key: 'HIGH', value: 'High' },
        { key: 'RESTRICTED', value: 'Restricted' },
        { key: 'PROHIBITED', value: 'Prohibited' },
    ];

    return (
        <PageContent heading="Add Merchant Category Code">
            <MetaTags title="Add MCC" description="Add Mcc page" />
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
            >
                {(props: FormikProps<CreateMccInput>) => {
                    return (
                        <Form>
                            <Stack spacing={5}>
                                <MainCard boxShadow>
                                    <h2 className="font-normal mb-0 mt-10">MCC Details</h2>
                                    <p>Enter Merchant Category Code details</p>
                                    <Box className="mt-20">
                                        <Stack spacing={4} sx={{ marginBottom: '40px' }}>
                                            <Box>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12} md={6}>
                                                        <InputBaseField
                                                            label="Merchant Category Code"
                                                            name="code"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <SelectBaseField
                                                            label="Risk Classification"
                                                            name="risk"
                                                            inputLabel="Select Risk Classification"
                                                            menuItems={riskMenuItems}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                            <Box>
                                                <Grid
                                                    container
                                                    spacing={3}
                                                    sx={{ height: '110px' }}
                                                >
                                                    <Grid item xs={12} md={6}>
                                                        <InputBaseField
                                                            label="MCC Friendly Name"
                                                            name="name"
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                            <Box>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12} md={6}>
                                                        <TextAreaInput
                                                            name="mccDescription"
                                                            placeholder="Enter MCC Description"
                                                            label="MCC Description"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <TextEditor
                                                            label="Supporting Documents"
                                                            placeholder="Enter all documents that will be uploaded along side this application..."
                                                            setTextValues={setDocumentsTextValues}
                                                            errorMessage={documentsErrorMessage}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Stack>
                                    </Box>
                                </MainCard>
                                <Box>
                                    <div className="flex space-end">
                                        <Button
                                            onClick={() => {
                                                return navigate('/mccs');
                                            }}
                                            className="reset-btn btn-medium mr-20"
                                            variant="contained"
                                            size="medium"
                                            disableElevation
                                        >
                                            Cancel
                                        </Button>
                                        {loading ? (
                                            <LoadingButton
                                                className="btn-medium"
                                                style={{
                                                    backgroundColor: '#F18F34',
                                                    color: '#fff',
                                                    padding: '5px 30px 5px 10px',
                                                }}
                                                loading
                                                loadingPosition="start"
                                                startIcon={<SaveIcon />}
                                                variant="contained"
                                            >
                                                Add MCC
                                            </LoadingButton>
                                        ) : (
                                            <>
                                                <Button
                                                    className={`${
                                                        props.dirty && props.isValid
                                                            ? 'btn-primary'
                                                            : 'btn-dark'
                                                    } btn-medium`}
                                                    type="submit"
                                                    variant="contained"
                                                    size="medium"
                                                    disableElevation
                                                    onClick={() => {
                                                        if (
                                                            !props.values.risk ||
                                                            !props.values.code ||
                                                            !props.values.name ||
                                                            !props.values.mccDescription
                                                        ) {
                                                            showErrorToast(
                                                                'Please fill in required fields'
                                                            );
                                                        }
                                                    }}
                                                >
                                                    Add MCC
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </Box>
                            </Stack>
                        </Form>
                    );
                }}
            </Formik>
        </PageContent>
    );
};

export default AddMccPage;
